import { useState, type ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ChevronDown, ChevronUp } from 'lucide-react';
import clsx from 'clsx';
import { Control, useWatch } from 'react-hook-form';
import { EstimateFormData } from '@/types/estimates/EstimateFormData';
import StyledTooltipContainer from '../styled/StyledTooltipContainer';
import EditIcon from '@/assets/icons/edit-01.svg?react';
import StyledTextarea from '../styled/StyledTextarea';
import StyledButton from '../styled/StyledButton';
import { useTranslation } from '@/i18n';
import {
  agencyMutedFieldBoxClass,
  agencyMutedFieldContentClass,
  agencyMutedLabelClass,
} from '@/utils/estimates/agencyMutedFieldStyles';

interface CircuitDetailsEditProps {
  control: Control<EstimateFormData>;
  name:
    | 'summary'
    | 'importantPoints'
    | 'priceIncludes'
    | 'priceExcludes'
    | 'modificationsConditions'
    | 'cancellationConditions';
  isViewMode?: boolean;
  onValidateParagraph?: (value: string) => Promise<void>;
}

interface CircuitDetailsSummaryCardProps {
  summary: string;
  title: string;
  textBrand?: boolean;
  muted?: boolean;
  edit?: CircuitDetailsEditProps;
}

const CircuitDetailsSummaryCard = ({
  summary,
  title,
  textBrand = false,
  muted = false,
  edit,
}: CircuitDetailsSummaryCardProps) => {
  if (edit !== undefined) {
    return (
      <CircuitDetailsSummaryCardWithEdit
        summary={summary}
        title={title}
        textBrand={textBrand}
        muted={muted}
        edit={edit}
      />
    );
  }

  if (summary.length === 0) return null;

  return (
    <CircuitDetailsSummaryCardShell
      title={title}
      textBrand={textBrand}
      muted={muted}
    >
      <p
        className={clsx({
          [agencyMutedFieldBoxClass]: muted,
          [agencyMutedFieldContentClass]: muted,
          'whitespace-pre-wrap': !muted,
        })}
      >
        {summary}
      </p>
    </CircuitDetailsSummaryCardShell>
  );
};

export default CircuitDetailsSummaryCard;

const CircuitDetailsSummaryCardWithEdit = ({
  summary,
  title,
  textBrand = false,
  muted = false,
  edit,
}: Required<Pick<CircuitDetailsSummaryCardProps, 'edit'>> &
  Omit<CircuitDetailsSummaryCardProps, 'edit'>) => {
  const watchedSummary = useWatch({
    control: edit.control,
    name: `circuit.${edit.name}`,
  });

  const effectiveSummary =
    typeof watchedSummary === 'string' && watchedSummary.trim().length > 0
      ? watchedSummary
      : summary;

  if (effectiveSummary.length === 0 && edit.isViewMode) return null;

  return (
    <CircuitDetailsSummaryCardShell
      title={title}
      textBrand={textBrand}
      muted={muted}
    >
      <CircuitDetailsSummaryCardEdit {...edit} muted={muted} />
    </CircuitDetailsSummaryCardShell>
  );
};

const CircuitDetailsSummaryCardShell = ({
  title,
  textBrand = false,
  muted = false,
  children,
}: {
  title: string;
  textBrand?: boolean;
  muted?: boolean;
  children: ReactNode;
}) => {
  const [isFolded, setIsFolded] = useState(false);

  return (
    <Card className="p-4">
      <CardHeader className="flex flex-row justify-between">
        <CardTitle
          className={clsx('text-lg font-medium', {
            [agencyMutedLabelClass]: muted,
            'text-brand-700': textBrand && !muted,
          })}
        >
          {title}
        </CardTitle>
        {isFolded ? (
          <ChevronDown
            className="size-5 hover:cursor-pointer"
            onClick={() => setIsFolded(false)}
          />
        ) : (
          <ChevronUp
            className="size-5 hover:cursor-pointer"
            onClick={() => setIsFolded(true)}
          />
        )}
      </CardHeader>
      {!isFolded && <CardContent>{children}</CardContent>}
    </Card>
  );
};

const CircuitDetailsSummaryCardEdit = ({
  control,
  name,
  isViewMode = false,
  onValidateParagraph,
  muted = false,
}: CircuitDetailsEditProps & { muted?: boolean }) => {
  const { t } = useTranslation();
  const [isEditEnabled, setIsEditEnabled] = useState(false);

  const field = useWatch({
    control,
    name: `circuit.${name}`,
  });

  const handleValider = async () => {
    if (onValidateParagraph && field != null) {
      await onValidateParagraph(field as string);
    }
    setIsEditEnabled(false);
  };

  return !isEditEnabled ? (
    <div className="flex justify-between items-start gap-6 w-full">
      <div
        className={clsx('w-full font-normal', {
          [agencyMutedFieldBoxClass]: muted,
          [agencyMutedFieldContentClass]: muted,
          'whitespace-pre-wrap text-base text-gray-800': !muted,
        })}
      >
        {field}
      </div>
      {!isViewMode && (
        <StyledTooltipContainer tooltipMessage="Modifier">
          <EditIcon
            className="size-4 hover:cursor-pointer stroke-brand-700"
            onClick={() => setIsEditEnabled(true)}
          />
        </StyledTooltipContainer>
      )}
    </div>
  ) : (
    <div className="flex flex-col gap-2 items-end">
      <StyledTextarea
        control={control}
        name={`circuit.${name}`}
        title={t('circuits.editorial.details.text')}
        rows={3}
      />
      <div>
        <StyledButton
          className="px-3 py-1.5 rounded-lg"
          title={t('common.validate')}
          onClick={handleValider}
        />
      </div>
    </div>
  );
};
